import React, { useContext, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import axios from 'axios';

import { AuthorizeContext } from '../RootContext';
import { api_url } from '../authorize';

import bg from '../img/background.jpg';
import ver from '../img/Version01.jpg';
import md from '../img/md.jpg';
import udi from '../img/udi.jpg';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    width: '100%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: 24,

    // Keep the original page background exactly as before.
    backgroundImage: `url(${bg})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',

    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      overflowY: 'auto',
      padding: '32px 14px',
    },
  },

  card: {
    width: '100%',
    maxWidth: 450,
    overflow: 'hidden',
    borderRadius: 14,
    background: '#ffffff',
    border: '1px solid rgba(15, 45, 70, 0.16)',
    boxShadow: '0 22px 65px rgba(4, 24, 43, 0.34)',
  },

  cardHeader: {
    position: 'relative',
    padding: '25px 30px 23px',
    color: '#ffffff',
    background:
      'linear-gradient(135deg, #102f4c 0%, #174f73 58%, #147e9b 100%)',

    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 3,
      background:
        'linear-gradient(90deg, #5ed0e6 0%, #ffffff 50%, #5ed0e6 100%)',
      opacity: 0.7,
    },

    [theme.breakpoints.down('xs')]: {
      padding: '22px 22px 20px',
    },
  },

  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },

  logoWrap: {
    width: 48,
    height: 48,
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: 7,
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.12)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
  },

  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },

  systemName: {
    margin: 0,
    fontSize: 21,
    lineHeight: 1.15,
    fontWeight: 800,
    letterSpacing: '0.01em',
  },

  companyName: {
    margin: '5px 0 0',
    color: 'rgba(255, 255, 255, 0.76)',
    fontSize: 12,
    letterSpacing: '0.035em',
  },

  cardBody: {
    padding: '31px 31px 25px',

    [theme.breakpoints.down('xs')]: {
      padding: '24px 22px 22px',
    },
  },

  title: {
    margin: 0,
    color: '#10283d',
    fontSize: 25,
    lineHeight: 1.2,
    fontWeight: 800,
    letterSpacing: '-0.025em',
  },

  subtitle: {
    margin: '8px 0 22px',
    color: '#6b7d89',
    fontSize: 13,
    lineHeight: 1.55,
  },

  udiBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 22,
    padding: '10px 12px',
    borderRadius: 9,
    background: '#f4f8fa',
    border: '1px solid #e0e9ee',
    color: '#687a86',
    fontSize: 10,
    lineHeight: 1.35,

    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
    },
  },

  udiImage: {
    maxWidth: 42,
    maxHeight: 20,
    objectFit: 'contain',
    flex: '0 0 auto',
  },

  field: {
    marginBottom: 17,

    '& .MuiInputLabel-root': {
      color: '#526777',
      fontWeight: 600,
    },

    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: '#ffffff',
      transition: 'box-shadow 160ms ease',

      '& fieldset': {
        borderColor: '#cbd7de',
      },

      '&:hover fieldset': {
        borderColor: '#83aebe',
      },

      '&.Mui-focused': {
        boxShadow: '0 0 0 4px rgba(15, 155, 188, 0.12)',
      },

      '&.Mui-focused fieldset': {
        borderColor: '#0f93b2',
        borderWidth: 1,
      },
    },

    '& input': {
      color: '#173247',
      fontSize: 14,
    },
  },

  error: {
    margin: '0 0 16px',
    padding: '10px 12px',
    borderRadius: 9,
    border: '1px solid #efc0bc',
    background: '#fff4f3',
    color: '#a23e38',
    fontSize: 12,
    lineHeight: 1.45,
    textAlign: 'center',
  },

  submit: {
    height: 46,
    borderRadius: 10,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #109bb9 0%, #08728c 100%)',
    boxShadow: '0 10px 22px rgba(8, 114, 140, 0.25)',
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: '0.02em',
    textTransform: 'none',

    '&:hover': {
      background: 'linear-gradient(135deg, #0d8eaa 0%, #065f75 100%)',
      boxShadow: '0 12px 26px rgba(8, 114, 140, 0.32)',
    },

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.78)',
      background: '#98aeb7',
    },
  },

  linkRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    marginTop: 14,

    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: 8,
    },
  },

  createAccount: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#7a8b96',
    fontSize: 12,
  },

  link: {
    color: '#087f9c',
    fontSize: 12,
    fontWeight: 700,
    textDecoration: 'none',

    '&:hover': {
      color: '#075e74',
      textDecoration: 'underline',
    },
  },

  security: {
    marginTop: 18,
    paddingTop: 15,
    borderTop: '1px solid #e3eaee',
    color: '#82919a',
    fontSize: 10,
    lineHeight: 1.5,
    textAlign: 'center',
  },

  compliance: {
    padding: '15px 22px 16px',
    background: '#f3f7f9',
    borderTop: '1px solid #dde7ec',
  },

  certificationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 13,
  },

  certificationImage: {
    maxWidth: 76,
    maxHeight: 27,
    objectFit: 'contain',
  },

  version: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: '#667680',
    fontSize: 10,
  },

  versionImage: {
    maxWidth: 36,
    maxHeight: 20,
    objectFit: 'contain',
  },

  resourceLink: {
    color: '#087f9c',
    fontSize: 10,
    fontWeight: 700,
    textDecoration: 'none',

    '&:hover': {
      color: '#075e74',
      textDecoration: 'underline',
    },
  },

  warning: {
    margin: '9px 0 0',
    color: '#ae4943',
    fontSize: 9,
    lineHeight: 1.4,
    textAlign: 'center',
  },
}));

const Login = () => {
  const classes = useStyles();

  const {
    setAuthBody,
    authBody,
    authenticated,
    setAuthenticated,
  } = useContext(AuthorizeContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onChange = (event) => {
    const { name, value } = event.target;

    setAuthBody({
      ...(authBody || {}),
      [name]: value,
    });

    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const username = authBody && authBody.username
      ? String(authBody.username).trim()
      : '';

    const password = authBody && authBody.password
      ? String(authBody.password)
      : '';

    if (!username || !password) {
      setError('Please enter both your username and password.');
      return;
    }

    try {
      setLoading(true);

      const response = await axios({
        method: 'POST',
        url: `https://${api_url}/api/login`,
        data: {
          ...authBody,
          username: username.toLowerCase(),
        },
      });

      if (response.data && response.data.token) {
        sessionStorage.setItem('token', response.data.token);
        sessionStorage.setItem('authenticated', 'true');

        setAuthenticated(true);
        window.location.reload();
        return;
      }

      setError(
        (response.data && response.data.message)
          || 'The username or password was not accepted.'
      );
    } catch (requestError) {
      setError(
        (requestError.response
          && requestError.response.data
          && requestError.response.data.message)
          || requestError.message
          || 'Unable to sign in. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (authenticated === true) {
    return <Redirect to="/" />;
  }

  return (
    <main className={classes.page}>
      <section className={classes.card}>
        <div className={classes.cardBody}>
          <h2 className={classes.title}>Sign in</h2>
          <p className={classes.subtitle}>
            Enter your company credentials to access the system.
          </p>

          <div className={classes.udiBox}>
            <img
              className={classes.udiImage}
              src={udi}
              alt="UDI"
            />
            <span>Basic UDI-DI: ++G283PtoleMedicSystemFC</span>
          </div>

          <form onSubmit={handleSubmit}>
            <TextField
              className={classes.field}
              id="username"
              name="username"
              label="Username"
              type="text"
              variant="outlined"
              autoComplete="username"
              value={(authBody && authBody.username) || ''}
              onChange={onChange}
              fullWidth
              required
            />

            <TextField
              className={classes.field}
              id="password"
              name="password"
              label="Password"
              type="password"
              variant="outlined"
              autoComplete="current-password"
              value={(authBody && authBody.password) || ''}
              onChange={onChange}
              fullWidth
              required
            />

            {error && (
              <div className={classes.error} role="alert">
                {error}
              </div>
            )}

            <Button
              className={classes.submit}
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>

            <div className={classes.linkRow}>
              <div className={classes.createAccount}>
                <span>Need access?</span>
                <Link className={classes.link} to="/new-user">
                  New user
                </Link>
              </div>

              <Link className={classes.link} to="/reset-password">
                Reset password
              </Link>
            </div>

            <div className={classes.security}>
              Secure company system. Unauthorized access is prohibited.
            </div>
          </form>
        </div>

        <footer className={classes.compliance}>
          <div className={classes.certificationRow}>
            <img
              className={classes.certificationImage}
              src={md}
              alt="Medical device"
            />

            <span className={classes.version}>
              <img
                className={classes.versionImage}
                src={ver}
                alt="Version"
              />
              Ver 1.3.1
            </span>

            <a
              className={classes.resourceLink}
              href="https://lentomedical.com/non-us-resources/"
              target="_blank"
              rel="noopener noreferrer"
            >
              기재사항 등
            </a>
          </div>

          <p className={classes.warning}>
            *Cutting guide not available for sale in the U.S.A.
          </p>
        </footer>
      </section>
    </main>
  );
};

export default Login;